import { format } from 'date-fns'
import { AlertTriangle, CircleCheck } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import { Modal } from '@/components/admin/shared/modal'
import {
  ingestionInputFromCompletedWork,
  isAttemptIngestibleForEngine,
} from './_lib/derivations'
import { DiffMarker } from './diff-marker'
import { useRecommendationEngineMock } from './recommendation-engine-context'
import type { CompletedWorkItem, ReportedApproach } from './types'

const GRADE_LABEL: Record<number, string> = {
  1: 'Again',
  2: 'Hard',
  3: 'Good',
  4: 'Easy',
}

const GRADE_VARIANT: Record<number, string> = {
  1: 'Hard',
  2: 'paused',
  3: 'Medium',
  4: 'active',
}

/** Sentinel dropdown value for the free-text "Other" approach. */
const OTHER = '__other__'

function ChevronToggle({ open }: { open: boolean }) {
  return (
    <span className="text-muted-foreground shrink-0 text-xs">
      {open ? '▾' : '▸'}
    </span>
  )
}

function CompletedWorkRow({
  work,
  onRequestReview,
  approachDiff,
}: {
  work: CompletedWorkItem
  onRequestReview: (id: string) => void
  approachDiff?: {
    previousValue: string | number
    currentValue: string | number
  }
}) {
  const {
    updateReportedApproach,
    engineEnabled,
    knowledgeMapSeeded,
    baselineEpochAt,
  } = useRecommendationEngineMock()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftType, setDraftType] = useState('')
  const [draftOtherText, setDraftOtherText] = useState('')

  const startEdit = () => {
    const ra = work.reportedApproach
    if (ra?.isPreselected) {
      setDraftType(ra.type)
      setDraftOtherText('')
    } else if (ra) {
      setDraftType(OTHER)
      setDraftOtherText(ra.otherText ?? ra.type)
    } else {
      setDraftType('')
      setDraftOtherText('')
    }
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraftType('')
    setDraftOtherText('')
  }

  const saveEdit = () => {
    let approach: ReportedApproach
    if (draftType === OTHER) {
      approach = {
        type: draftOtherText.trim(),
        isPreselected: false,
        otherText: draftOtherText.trim(),
      }
    } else {
      approach = { type: draftType, isPreselected: true }
    }
    updateReportedApproach(work.id, approach)
    setEditing(false)
    setDraftType('')
    setDraftOtherText('')
  }

  const saveDisabled =
    draftType === '' || (draftType === OTHER && draftOtherText.trim() === '')
  const eligibility = isAttemptIngestibleForEngine(
    ingestionInputFromCompletedWork(work, {
      knowledgeMapSeeded,
      baselineEpochAt,
    }),
  )
  const isExcludedFromEngine = !work.needsGrading && !eligibility.eligible

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => {
          if (open) cancelEdit()
          setOpen(!open)
        }}
        className="hover:bg-muted/50 flex w-full items-center gap-3 p-3 text-left transition-colors"
      >
        <ChevronToggle open={open} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground text-sm font-medium">
              {work.problemTitle}
            </span>
            <Badge variant={work.difficulty}>{work.difficulty}</Badge>
            {work.needsGrading && <Badge variant="paused">Needs grading</Badge>}
            {isExcludedFromEngine && <Badge variant="Hard">Excluded</Badge>}
          </div>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {work.topicName} → {work.subtopicName} · {work.source} ·{' '}
            {format(new Date(work.completedAt), 'MMM d, yyyy')}
          </p>
        </div>
        <div className="text-muted-foreground hidden shrink-0 gap-4 text-xs sm:flex">
          <span>
            {work.durationMinutes}m
            <span className="text-muted-foreground/60"> time</span>
          </span>
          <span>
            {work.runCount}/{work.submitCount}
            <span className="text-muted-foreground/60"> run/sub</span>
          </span>
          <span className="w-16 text-right">
            {work.selfScore ? (
              <Badge variant={GRADE_VARIANT[work.selfScore]}>
                {GRADE_LABEL[work.selfScore]}
              </Badge>
            ) : (
              <span className="text-muted-foreground/60">no rating</span>
            )}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-border bg-muted/20 space-y-3 border-t p-3">
          {/* Reported approach */}
          <div>
            <p className="text-foreground mb-1 text-xs font-semibold">
              Reported approach
            </p>
            {editing ? (
              <div className="space-y-2">
                <select
                  value={draftType}
                  onChange={(e) => {
                    const nextType = e.target.value
                    setDraftType(nextType)
                    if (nextType === OTHER) {
                      const existingOther =
                        work.reportedApproach &&
                        !work.reportedApproach.isPreselected
                          ? (work.reportedApproach.otherText ??
                            work.reportedApproach.type)
                          : ''
                      setDraftOtherText(existingOther)
                    }
                  }}
                  disabled={!engineEnabled}
                  aria-label="Reported approach"
                  className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs"
                >
                  <option value="">Select an approach…</option>
                  {work.knownApproaches.map((approach) => (
                    <option key={approach} value={approach}>
                      {approach}
                    </option>
                  ))}
                  <option value={OTHER}>Other…</option>
                </select>
                {draftType === OTHER && (
                  <input
                    type="text"
                    value={draftOtherText}
                    onChange={(e) => setDraftOtherText(e.target.value)}
                    disabled={!engineEnabled}
                    placeholder="Describe the approach"
                    aria-label="Other approach description"
                    className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs"
                  />
                )}
              </div>
            ) : work.reportedApproach ? (
              <div className="flex flex-wrap items-start gap-2">
                <Badge
                  variant={
                    work.reportedApproach.isPreselected ? 'optimal' : 'premium'
                  }
                >
                  {work.reportedApproach.type}
                </Badge>
                {approachDiff && (
                  <DiffMarker
                    previousValue={approachDiff.previousValue}
                    currentValue={approachDiff.currentValue}
                  />
                )}
                <span className="text-muted-foreground text-xs">
                  {work.reportedApproach.isPreselected
                    ? 'Matched a preselected approach for this problem.'
                    : 'Reported as "Other" — coach should review whether this is a new approach worth adding.'}
                </span>
                {work.reportedApproach.otherText && (
                  <p className="bg-background border-border w-full rounded-md border p-2 text-xs italic">
                    "{work.reportedApproach.otherText}"
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">
                No approach reported — postmortem was skipped.
              </p>
            )}
          </div>

          {/* Submitted code */}
          <div>
            <p className="text-foreground mb-1 text-xs font-semibold">
              Submitted code
            </p>
            <pre className="bg-background border-border overflow-x-auto rounded-md border p-3 text-xs">
              <code>{work.code}</code>
            </pre>
          </div>

          {/* Grade action footer */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-xs">
              Self-rating:{' '}
              {work.selfScore ? (
                <span className="text-foreground font-medium">
                  {GRADE_LABEL[work.selfScore]} ({work.selfScore})
                </span>
              ) : (
                <span className="text-foreground font-medium">none</span>
              )}
            </span>
            {work.needsGrading ? (
              <div className="flex items-center gap-2">
                {editing ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={!engineEnabled}
                      className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md px-3 py-1.5 text-xs transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={saveDisabled || !engineEnabled}
                      className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEdit}
                    disabled={!engineEnabled}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md px-3 py-1.5 text-xs transition-colors"
                  >
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRequestReview(work.id)}
                  disabled={editing || !engineEnabled}
                  className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Mark reviewed
                </button>
              </div>
            ) : (
              <div className="text-right">
                <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                  <CircleCheck size={13} />
                  Reviewed
                </span>
                {!eligibility.eligible && (
                  <p className="text-amber-700 dark:text-amber-300 mt-1 text-[11px]">
                    Excluded from engine: {eligibility.reasons.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Homework Review view — completed attempts the coach reviews so the engine can
 * pick up the graded signal. The coach can correct a poorly-reported approach
 * before reviewing, and Mark Reviewed is gated behind an "are you sure?" modal.
 */
export function HomeworkReviewView() {
  const { completedWork, markReviewed, engineEnabled, pendingDiff } =
    useRecommendationEngineMock()
  const [confirmWorkId, setConfirmWorkId] = useState<string | null>(null)

  const pendingCount = useMemo(
    () => completedWork.filter((w) => w.needsGrading).length,
    [completedWork],
  )

  const confirmWork = completedWork.find((w) => w.id === confirmWorkId) ?? null

  const handleProceed = () => {
    if (confirmWorkId) markReviewed(confirmWorkId)
    setConfirmWorkId(null)
  }

  return (
    <div className="space-y-4">
      {pendingCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertTriangle
            size={18}
            className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
          />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {pendingCount} attempt{pendingCount === 1 ? '' : 's'} waiting for
              review
            </p>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80">
              Reviewing graded work feeds the engine — recommendations and
              progress only update once an attempt is reviewed.
            </p>
          </div>
        </div>
      )}
      {!engineEnabled && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          Starting date must be selected first.
        </div>
      )}

      <div className="space-y-2">
        {completedWork.map((w) => (
          <CompletedWorkRow
            key={w.id}
            work={w}
            onRequestReview={setConfirmWorkId}
            approachDiff={pendingDiff.find(
              (item) => item.key === `homework-approach:${w.id}`,
            )}
          />
        ))}
      </div>

      <Modal
        isOpen={confirmWorkId !== null}
        onClose={() => setConfirmWorkId(null)}
        title="Mark attempt as reviewed?"
      >
        <p className="text-muted-foreground text-sm">
          Are you sure? Marking{' '}
          <span className="text-foreground font-medium">
            {confirmWork?.problemTitle}
          </span>{' '}
          as reviewed feeds its graded signal into the engine and recalculates
          this client&apos;s knowledge map.
        </p>
        <div className="border-border mt-4 flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={() => setConfirmWorkId(null)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md px-3 py-1.5 text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleProceed}
            className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90"
          >
            Proceed
          </button>
        </div>
      </Modal>
    </div>
  )
}
